import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@shared-frontend/components/ui/card";
import { Button } from "@shared-frontend/components/ui/button";
import { Input } from "@shared-frontend/components/ui/input";
import { Label } from "@shared-frontend/components/ui/label";
import { Alert, AlertDescription } from "@shared-frontend/components/ui/alert";
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { useValidateMagicLinkToken } from "@/hooks/queries/useValidateMagicLinkToken";
import { useVerifyMagicLinkMutation } from "@/hooks/mutations/useVerifyMagicLinkMutation";
import { VerifyEmailPageSkeleton } from "./VerifyEmailPageSkeleton";
import helmguardLogo from "@/assets/helmGuardBlue.svg";

export function VerifyEmailPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    // Validate token
    const {
        data: validationData,
        isLoading: isValidating,
        error: validationError,
    } = useValidateMagicLinkToken(token);

    // Verify magic link mutation
    const verifyMutation = useVerifyMagicLinkMutation();

    // Calculate password strength
    useEffect(() => {
        if (!password) {
            setPasswordStrength(0);
            return;
        }

        let strength = 0;
        if (password.length >= 8) strength += 1;
        if (/[a-z]/.test(password)) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;

        setPasswordStrength(strength);
    }, [password]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            return;
        }

        if (passwordStrength < 3) {
            return;
        }

        try {
            await verifyMutation.mutateAsync({
                token: token!,
                password,
            });

            // Redirect to success page
            navigate("/password-set-success");
        } catch (error) {
            // Error is handled by the mutation
        }
    };

    // Show skeleton while validating
    if (isValidating) {
        return <VerifyEmailPageSkeleton />;
    }

    // Show error if token is invalid
    if (validationError || !validationData?.success) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                        <CardTitle className="text-2xl">
                            Invalid Magic Link
                        </CardTitle>
                        <CardDescription>
                            This magic link is invalid or has expired.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Button
                            onClick={() => navigate("/login")}
                            className="w-full bg-foreground text-background hover:bg-foreground/90"
                        >
                            Return to Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show password form if token is valid
    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="flex justify-center mb-4">
                        <img
                            src={helmguardLogo}
                            alt="HelmGuard"
                            className="h-16 w-auto"
                        />
                    </div>
                    <CardTitle className="text-2xl font-semibold text-gray-900">
                        Change Your Password
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                        Enter a new password below.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* New Password Field */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="password"
                                className="text-gray-700 font-medium"
                            >
                                New password *
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    className="pr-10"
                                    placeholder="Enter your new password"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password Field */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="confirmPassword"
                                className="text-gray-700 font-medium"
                            >
                                Re-enter new password *
                            </Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={
                                        showConfirmPassword
                                            ? "text"
                                            : "password"
                                    }
                                    value={confirmPassword}
                                    onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    className="pr-10"
                                    placeholder="Re-enter your new password"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowConfirmPassword(
                                            !showConfirmPassword,
                                        )
                                    }
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password Requirements */}
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">
                                Your password must contain:
                            </h4>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-green-600">
                                    <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                    At least 8 characters
                                </div>
                                <div className="flex items-center text-sm text-green-600">
                                    <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                    At least 3 of the following:
                                </div>
                                <div className="ml-6 space-y-1">
                                    <div className="flex items-center text-sm text-green-600">
                                        <CheckCircle className="h-3 w-3 mr-2 flex-shrink-0" />
                                        Lower case letters (a-z)
                                    </div>
                                    <div className="flex items-center text-sm text-green-600">
                                        <CheckCircle className="h-3 w-3 mr-2 flex-shrink-0" />
                                        Upper case letters (A-Z)
                                    </div>
                                    <div className="flex items-center text-sm text-green-600">
                                        <CheckCircle className="h-3 w-3 mr-2 flex-shrink-0" />
                                        Numbers (0-9)
                                    </div>
                                    <div className="flex items-center text-sm text-green-600">
                                        <CheckCircle className="h-3 w-3 mr-2 flex-shrink-0" />
                                        Special characters (e.g. !@#$%^&*)
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Error Messages */}
                        {password &&
                            confirmPassword &&
                            password !== confirmPassword && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        Passwords do not match. Please try
                                        again.
                                    </AlertDescription>
                                </Alert>
                            )}

                        {password && passwordStrength < 3 && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    Password does not meet the minimum
                                    requirements.
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Mutation Error */}
                        {verifyMutation.isError && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    Something went wrong. Please try again.
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                            disabled={
                                verifyMutation.isPending ||
                                passwordStrength < 3 ||
                                password !== confirmPassword
                            }
                        >
                            {verifyMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Resetting password...
                                </>
                            ) : (
                                "Reset password"
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
